/**
 * Atajos de teclado.
 *
 * Un POS se opera con las manos en el teclado y en el lector de barras, no en el
 * ratón. El criterio de aceptación de la fase es "veinte ventas seguidas sin
 * tocar el mouse", y eso depende de este archivo.
 *
 * Dos detalles que marcan la diferencia:
 *
 * * **Cuando el foco está en un campo de texto, los atajos de una sola letra no
 *   se disparan.** Si no, escribir "Fanta" en el buscador borraría el carrito al
 *   llegar a la "f".
 * * **Las teclas de función sí funcionan siempre**, incluso escribiendo: el
 *   cajero pulsa F4 para cobrar sin levantar los dedos del teclado numérico.
 * * **Y `Enter` también.** Lo encontró un test: al principio `Enter` estaba
 *   bloqueado mientras se escribía, y eso rompía justo el gesto más importante
 *   del POS —el lector de barras escribe el código y pulsa `Enter`—. Bloquear
 *   "las teclas de escribir" estaba bien; bloquear `Enter` era un bug.
 *
 * Las claves del mapa se escriben en su forma natural (`F4`, `Enter`,
 * `ArrowDown`) y el gancho las normaliza: exigir minúsculas al registrar era
 * una trampa en la que ya caímos una vez.
 */

import { useEffect, useRef } from "react";

export type ShortcutHandler = (event: KeyboardEvent) => void;

export interface Shortcuts {
  [combo: string]: ShortcutHandler | undefined;
}

const FUNCTION_KEYS = /^F\d{1,2}$/;

/**
 * Teclas que se pueden robar al buscador sin molestar a quien escribe.
 *
 * En un POS el foco vive en el buscador, así que "mientras se escribe no se
 * disparan atajos" convertía en inútiles los atajos del carrito. Estas teclas
 * no producen texto y en un campo de una sola línea no hacen nada útil:
 *
 * * `↑`/`↓` sólo mueven el cursor al principio o al final.
 * * `Escape` se usa para cancelar.
 *
 * `Supr`, `←`, `→`, `Inicio` y `Fin` **no** están aquí a propósito: mientras se
 * corrige un código, esas teclas tienen que editar el texto.
 */
const NAVIGATION_KEYS = new Set(["ArrowUp", "ArrowDown", "Escape"]);

/** ¿El evento viene de un campo donde se está escribiendo? */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

/** Normaliza un evento a una clave tipo "F2", "ctrl+enter" o "Escape". */
export function comboOf(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.ctrlKey) parts.push("ctrl");
  if (event.altKey) parts.push("alt");
  if (event.shiftKey && event.key.length > 1) parts.push("shift");
  parts.push(event.key);
  return parts.join("+").toLowerCase();
}

/**
 * Registra atajos globales. Se puede desactivar (por ejemplo mientras hay un
 * diálogo abierto con sus propios atajos).
 */
export function useShortcuts(shortcuts: Shortcuts, enabled = true): void {
  // Las claves se guardan en minúsculas porque `comboOf` devuelve siempre
  // minúsculas. Sin esta normalización, registrar `F4` o `Enter` —que es lo
  // natural de escribir— no enlazaba con nada y **ningún atajo funcionaba**.
  // Lo encontró el test de la venta completa con teclado.
  const ref = useRef<Record<string, ShortcutHandler>>({});
  ref.current = Object.fromEntries(
    Object.entries(shortcuts)
      .filter(([, handler]) => Boolean(handler))
      .map(([combo, handler]) => [combo.toLowerCase(), handler as ShortcutHandler]),
  );

  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent) {
      const combo = comboOf(event);
      const handler = ref.current[combo];
      if (!handler) return;

      const isFunctionKey = FUNCTION_KEYS.test(event.key);
      const hasModifier = event.ctrlKey || event.altKey || event.metaKey;
      const isEnter = event.key === "Enter";
      const isNavigation = NAVIGATION_KEYS.has(event.key);
      if (
        isTypingTarget(event.target) &&
        !isFunctionKey &&
        !hasModifier &&
        !isEnter &&
        !isNavigation
      ) {
        return;
      }

      // `preventDefault` evita que F5 recargue o que F3 abra el buscador del
      // navegador: en una caja, el navegador no debe llevarse ninguna tecla.
      event.preventDefault();
      handler(event);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}

/**
 * Atajos del POS, en un solo sitio para poder documentarlos, probarlos y
 * enseñarlos en pantalla.
 *
 * Las cantidades usan `F5`/`F6` y no `+`/`-` porque el foco vive en el buscador
 * y `+`/`-` tienen que poder escribirse. `+`/`-` siguen funcionando cuando el
 * foco está en el carrito, pero el camino garantizado es la tecla de función.
 */
export const POS_SHORTCUTS = {
  search: "F2",
  charge: "F4",
  decrease: "F5",
  increase: "F6",
  discount: "F8",
  clearCart: "F9",
  newSale: "F7",
  removeLine: "Delete",
  moveUp: "ArrowUp",
  moveDown: "ArrowDown",
  closeDialog: "Escape",
  addFirst: "Enter",
} as const;
